/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  FormControl,
  Input,
  Button,
  useColorModeValue,
  Progress,
  Badge,
} from "@chakra-ui/react";
import { FiUsers, FiPackage, FiShoppingCart, FiBox, FiMessageSquare, FiEdit2 } from "react-icons/fi";
import api from "utils/api";
import { useToast } from "@chakra-ui/react";

export default function BayiDurumuModal({ isOpen, onClose, bayi, onUpdate }) {
  const toast = useToast();
  const textColor = useColorModeValue("gray.700", "white");
  const cardBg = useColorModeValue("gray.50", "navy.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  const [smsLimit, setSmsLimit] = useState(bayi?.sms_monthly_limit || "");
  const [isEditingSms, setIsEditingSms] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Modal açıldığında SMS limitini set et
  React.useEffect(() => {
    if (isOpen && bayi) {
      setSmsLimit(bayi.sms_monthly_limit || "");
      setIsEditingSms(false);
    }
  }, [isOpen, bayi]);

  const handleSaveSmsLimit = async () => {
    if (!bayi) return;

    setIsSaving(true);
    try {
      const updateData = {
        sms_monthly_limit: smsLimit === "" ? null : parseInt(smsLimit),
      };

      const response = await api.put(
        `/api/bayi-yonetimi/bayiler/${bayi.id}`,
        updateData
      );

      if (response.ok) {
        toast({
          title: "Başarılı",
          description: "SMS limiti başarıyla güncellendi",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setIsEditingSms(false);
        if (onUpdate) {
          onUpdate();
        }
      } else {
        const error = await response.json();
        throw new Error(error.detail || "SMS limiti güncellenemedi");
      }
    } catch (error) {
      toast({
        title: "Hata",
        description: error.message || "SMS limiti güncellenirken bir hata oluştu",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!bayi) return null;

  const smsUsageInfo = bayi.sms_usage_info || {};
  const usagePercentage = smsUsageInfo.usage_percentage || 0;
  const used = smsUsageInfo.used || 0;
  const limit = smsUsageInfo.limit || null;
  const remaining = smsUsageInfo.remaining || null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color={textColor}>Bayi Durumu: {bayi.username}</ModalHeader>
        <ModalCloseButton />
        <Divider />
        <ModalBody py={6}>
          <VStack spacing={6} align="stretch">
            {/* Bayi Durumu Bilgileri */}
            <Box>
              <Text fontSize="md" fontWeight="bold" color={textColor} mb={4}>
                Bayi Durumu
              </Text>
              <VStack spacing={3} align="stretch">
                {/* Firma */}
                <HStack
                  justify="space-between"
                  p={3}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack spacing={3}>
                    <Box color="green.500">
                      <FiUsers size={20} />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      Firma
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold" color="green.600">
                    {bayi.firma_count || 0}
                  </Text>
                </HStack>

                {/* Cihaz */}
                <HStack
                  justify="space-between"
                  p={3}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack spacing={3}>
                    <Box color="purple.500">
                      <FiPackage size={20} />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      Cihaz
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold" color="purple.600">
                    {bayi.device_count || 0}
                  </Text>
                </HStack>

                {/* Stok */}
                <HStack
                  justify="space-between"
                  p={3}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack spacing={3}>
                    <Box color="orange.500">
                      <FiShoppingCart size={20} />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      Stok
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold" color="orange.600">
                    {bayi.stock_count || 0}
                  </Text>
                </HStack>

                {/* Aksesuar */}
                <HStack
                  justify="space-between"
                  p={3}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack spacing={3}>
                    <Box color="blue.500">
                      <FiBox size={20} />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      Aksesuar
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold" color="blue.600">
                    {bayi.aksesuar_count || 0}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <Divider />

            {/* SMS Kullanımı */}
            <Box>
              <Text fontSize="md" fontWeight="bold" color={textColor} mb={4}>
                SMS Kullanımı
              </Text>
              <VStack spacing={4} align="stretch">
                {/* SMS Kullanım Bilgisi */}
                <Box
                  p={4}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack justify="space-between" mb={3}>
                    <HStack spacing={3}>
                      <Box color="teal.500">
                        <FiMessageSquare size={20} />
                      </Box>
                      <Text fontSize="sm" fontWeight="medium" color={textColor}>
                        Bu Ay Kullanılan
                      </Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {used} {limit ? `/ ${limit}` : ""}
                    </Text>
                  </HStack>

                  {limit !== null && (
                    <>
                      <Progress
                        value={usagePercentage}
                        colorScheme={
                          usagePercentage > 80
                            ? "red"
                            : usagePercentage > 60
                            ? "orange"
                            : "green"
                        }
                        size="sm"
                        borderRadius="full"
                        mb={2}
                      />
                      <HStack justify="space-between">
                        <Text fontSize="xs" color="gray.500">
                          Kalan: {remaining !== null ? remaining : "∞"}
                        </Text>
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={
                            usagePercentage > 80
                              ? "red.500"
                              : usagePercentage > 60
                              ? "orange.500"
                              : "green.500"
                          }
                        >
                          %{usagePercentage.toFixed(1)}
                        </Text>
                      </HStack>
                    </>
                  )}

                  {limit === null && (
                    <Badge colorScheme="green" fontSize="xs">
                      Limitsiz
                    </Badge>
                  )}
                </Box>

                {/* SMS Ayarı */}
                <Box
                  p={4}
                  bg={cardBg}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>
                      SMS Aylık Limiti
                    </Text>
                    {!isEditingSms && (
                      <Button
                        size="xs"
                        leftIcon={<FiEdit2 />}
                        onClick={() => setIsEditingSms(true)}
                        colorScheme="blue"
                        variant="ghost"
                      >
                        Düzenle
                      </Button>
                    )}
                  </HStack>

                  {isEditingSms ? (
                    <VStack spacing={3} align="stretch">
                      <FormControl>
                        <Input
                          type="number"
                          value={smsLimit}
                          onChange={(e) => setSmsLimit(e.target.value)}
                          placeholder="Boş bırakırsanız limitsiz olur"
                          size="sm"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Aylık SMS gönderme limiti. Boş bırakırsanız limitsiz olur.
                        </Text>
                      </FormControl>
                      <HStack>
                        <Button
                          size="sm"
                          onClick={() => {
                            setIsEditingSms(false);
                            setSmsLimit(bayi.sms_monthly_limit || "");
                          }}
                          variant="ghost"
                        >
                          İptal
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveSmsLimit}
                          isLoading={isSaving}
                          colorScheme="blue"
                        >
                          Kaydet
                        </Button>
                      </HStack>
                    </VStack>
                  ) : (
                    <Text fontSize="sm" color={textColor}>
                      {limit !== null ? `${limit} SMS/ay` : "Limitsiz"}
                    </Text>
                  )}
                </Box>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button onClick={onClose} colorScheme="blue">
            Kapat
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

